import { gql } from '@apollo/client'
import {
  FetchAgentCalendarQuery,
  LeasePropertyType,
  CalResponseStatus,
  ShowingStatus,
  ConfirmationStatus,
  UnitType,
  TourType,
  MessageType,
} from '../../../common/src/types.gen'

export const fetchAgentCalendarQuery = gql`
  query FetchAgentCalendar($id: String!, $date: String) {
    agentCalendar(id: $id, date: $date) {
      date
      isToday
      startOfDay
      endOfDay
      contact {
        id
        firstName
        lastName
        displayName
        addressEmail
      }
      workHours {
        lowerBound
        upperBound
      }
      offHours {
        lowerBound
        upperBound
      }
      calEvents {
        timeCreated
        timeStart
        timeEnd
        summary
        showingId
        attendees {
          calId
          responseStatus
        }
        source
      }
      showings {
        id
        timeStart
        showingStatus
        history {
          contactId
          confirmationStatus
          timeUpdated
        }
        showingContacts {
          confirmationStatus
          isAgent
          timeUpdated
          contact {
            id
            firstName
            lastName
            addressPhone
            addressEmail
            threads {
              transcriptUrl
              messageType
              leasingTask {
                id
              }
            }
            preferMessageType
          }
        }
        unitType
        tourType
        leasingTask {
          id
          displayName
          address {
            addressParts {
              street
              city
              state
            }
          }
          floorPlans {
            bedrooms
          }
          leasingInfo {
            showingDurationMins
            propertyType
          }
          firm {
            showingDurationMins
          }
        }
      }
      timeZone
    }
  }
`

const agentEmail = '12345@gmail.com'
export const mockShowing: FetchAgentCalendarQuery['agentCalendar']['showings'][0] = {
  __typename: 'Showing',
  id: 1222,
  timeStart: '2020-08-21 16:00:00',
  showingStatus: ShowingStatus.CREATED,
  history: [
    {
      __typename: 'ShowingContactHistory',
      contactId: 2220042,
      confirmationStatus: ConfirmationStatus.REJECTED,
      timeUpdated: '2020-08-20 16:00:00',
    },
  ],
  showingContacts: [
    {
      __typename: 'ShowingContact',
      confirmationStatus: ConfirmationStatus.CONFIRMED,
      isAgent: false,
      contact: {
        __typename: 'Contact',
        firstName: 'Mary',
        lastName: 'Doe',
        id: 8675309,
        addressPhone: '(234) 567-8990',
        addressEmail: 'marydoe@gmail.com',
        threads: [
          {
            __typename: 'Thread',
            transcriptUrl: 'https://dynasty.com/api/transcript?uuid=4a100cad-0582-4049-8cd9-3fb6199efbfc',
            messageType: MessageType.SMS,
            leasingTask: {
              __typename: 'LeasingTask',
              id: 17566,
            },
          },
        ],
        preferMessageType: MessageType.SMS,
      },
      timeUpdated: '2020-08-20 12:00:00',
    },
  ],
  unitType: UnitType.BR1,
  tourType: TourType.VIRTUAL,
  leasingTask: {
    __typename: 'LeasingTask',
    id: 17566,
    displayName: 'The hole at the shire',
    address: {
      __typename: 'Address',
      addressParts: {
        __typename: 'AddressParts',
        street: '1504 Bay Rd',
        city: 'Miami Beach',
        state: 'FL',
      },
    },

    floorPlans: [
      {
        __typename: 'FloorPlan',
        bedrooms: 2,
      },
    ],
    leasingInfo: {
      __typename: 'LeasingInfo' as 'LeasingInfo',
      showingDurationMins: 20,
      propertyType: LeasePropertyType.MF,
    },
    firm: {
      __typename: 'Firm' as 'Firm',
      showingDurationMins: 40,
    },
  },
}
export const agentId = 2220041
export const mockFetchAgentCalendar: FetchAgentCalendarQuery = {
  agentCalendar: {
    __typename: 'AgentCalendar',
    date: '2020-08-25',
    isToday: true,
    startOfDay: '2020-08-21T04:00:00Z',
    endOfDay: '2020-08-22T04:00:00Z',
    contact: {
      __typename: 'Contact',
      id: agentId,
      firstName: 'John',
      lastName: 'Doe',
      displayName: 'John Doe',
      addressEmail: agentEmail,
    },
    workHours: [
      {
        __typename: 'Range',
        lowerBound: '/2020-08-21T14:00:00Z\\',
        upperBound: '\\2020-08-21T22:00:00Z/',
      },
    ],
    offHours: [
      {
        __typename: 'Range',
        lowerBound: '/2020-08-21T04:00:00Z\\',
        upperBound: '\\2020-08-21T14:00:00Z/',
      },
      {
        __typename: 'Range',
        lowerBound: '/2020-08-21T22:00:00Z\\',
        upperBound: '\\2020-08-22T04:00:00Z/',
      },
    ],
    calEvents: [
      // showing event
      {
        __typename: 'CalEvent',
        timeCreated: '2020-08-15 08:00:00',
        timeStart: '2020-08-21 16:00:00',
        timeEnd: '2020-08-21 16:30:00',
        summary: 'Showing at 1213 Euclid Ave.',
        showingId: 1222,
        attendees: [
          {
            __typename: 'Attendee',
            calId: agentEmail,
            responseStatus: CalResponseStatus.ACCEPTED,
          },
          {
            __typename: 'Attendee',
            calId: 'bgipson@cambridgemgi.com',
            responseStatus: CalResponseStatus.ACCEPTED,
          },
        ],
        source: 'GOOGLE',
      },
      // busy event
      {
        __typename: 'CalEvent',
        timeCreated: '2020-08-19 10:00:00',
        timeStart: '2020-08-21 17:00:00',
        timeEnd: '2020-08-21 18:30:00',
        summary: '',
        showingId: 0,
        attendees: [
          {
            __typename: 'Attendee',
            calId: agentEmail,
            responseStatus: CalResponseStatus.ACCEPTED,
          },
        ],
        source: 'GOOGLE',
      },
      // free event
      {
        __typename: 'CalEvent',
        timeCreated: '2020-08-20 17:00:00',
        timeStart: '2020-08-21 19:00:00',
        timeEnd: '2020-08-21 20:00:00',
        summary: '',
        showingId: 0,
        attendees: [
          {
            __typename: 'Attendee',
            calId: agentEmail,
            responseStatus: CalResponseStatus.DECLINED,
          },
        ],
        source: null,
      },
    ],
    showings: [mockShowing],
    timeZone: 'America/Chicago',
  },
}
